/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-04T11:47:00+05:00
Intent: Show the admin's Arabic title and message when the panel is in Arabic, and resolve a notification's custom link safely.
*/

/**
 * The gateway stores notification titles as English text. Known titles map to an i18n key so the
 * card header follows the panel language; anything else is shown as sent.
 */
const TITLE_KEYS: Array<[RegExp, string]> = [
  [/plan\s+has\s+been\s+updated|تم تحديث خطتك/i, 'plan-updated'],
  [/plan\s+is\s+now\s+active|plan\s+(has\s+been\s+)?activated/i, 'plan-active'],
  [/(contest|campaign)s?\s+published/i, 'campaign-published'],
  [/(contest|campaign)s?\s+updated/i, 'campaign-updated'],
  [/(contest|campaign)s?\s+approved/i, 'campaign-approved'],
  [/(contest|campaign)s?\s+rejected/i, 'campaign-rejected'],
  [/(contest|campaign)s?\s+started/i, 'campaign-started'],
  [/(contest|campaign)s?\s+ended/i, 'campaign-ended'],
  [/winners?\s+(have\s+been\s+)?announced/i, 'winner-announced'],
];

/** Gateway events with a fixed header, for titles the patterns above do not recognise. */
const EVENT_KEYS: Record<string, string> = {
  plan_overridden: 'plan-updated',
  plan_activated: 'plan-active',
  contest_published: 'campaign-published',
  contest_published_client: 'campaign-published',
  contest_updated: 'campaign-updated',
  contest_approved: 'campaign-approved',
  contest_rejected: 'campaign-rejected',
  contest_started: 'campaign-started',
  contest_ended: 'campaign-ended',
  contest_winners_announced: 'winner-announced',
  winner_announced: 'winner-announced',
};

export function notificationTitleKey(title: string, event?: string | null): string | null {
  const text = (title || '').trim();
  const hit = TITLE_KEYS.find(([pattern]) => pattern.test(text));
  const key = hit ? hit[1] : EVENT_KEYS[String(event || '').trim().toLowerCase()];
  return key ? `notifications.titles.${key}` : null;
}

/** First non-blank string among the given fields. */
function firstText(item: any, fields: string[]): string {
  for (const field of fields) {
    const value = item?.[field];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

// The admin portal saves its Arabic copy as ar_*; older records use *_ar or *_arabic.
const AR_TITLE_FIELDS = ['ar_notification_title', 'notification_title_ar', 'notification_title_arabic', 'ar_notification_name', 'notification_name_ar'];
const AR_MESSAGE_FIELDS = ['ar_message', 'message_ar', 'message_arabic'];

const isArabic = (lang?: string | null) => String(lang || '').toLowerCase().startsWith('ar');

/**
 * The header of a notification in the panel language. An Arabic title written by the admin wins in
 * Arabic; otherwise known titles are translated and unknown ones keep the sender's text.
 */
export function localizedNotificationTitle(item: any, translate: (key: string) => string, lang?: string | null): string {
  const arabicTitle = isArabic(lang) ? firstText(item, AR_TITLE_FIELDS) : '';
  if (arabicTitle) return arabicTitle;
  const title = item?.notification_title || item?.notification_name || '';
  const key = notificationTitleKey(item?.notification_title || '', item?.event || item?.type);
  const text = key ? translate(key) : '';
  // Until the language file loads, instant() hands the key back; the sender's text reads better.
  return text && text !== key ? text : campaignWording(title);
}

/** The product calls them campaigns now; older gateway titles still say "contest". */
export function campaignWording(text: string): string {
  return (text || '').replace(/\bcontest(s?)\b/gi, (word, plural) => {
    const base = word[0] === 'C' ? (word === word.toUpperCase() ? 'CAMPAIGN' : 'Campaign') : 'campaign';
    return base + (plural ? (base === 'CAMPAIGN' ? 'S' : 's') : '');
  });
}

/** The message in the panel language; falls back to the English text when no Arabic copy was sent. */
export function localizedNotificationMessage(item: any, lang?: string | null): string {
  const arabicMessage = isArabic(lang) ? firstText(item, AR_MESSAGE_FIELDS) : '';
  return arabicMessage || item?.message || '';
}

/**
 * The custom link an admin attached ("Custom Link" deep link), as an absolute http(s) URL.
 * A bare domain such as "luqta.io" gets https://; any other scheme (javascript:, data:, …) is refused.
 */
export function notificationLink(item: any): string | null {
  // The admin form keeps the typed link even after switching back to "No Deep Link".
  const kind = String(item?.deep_link || '').trim().toLowerCase();
  if (kind && kind !== 'custom') return null;
  const raw = typeof item?.custom_deep_link === 'string' ? item.custom_deep_link.trim() : '';
  if (!raw) return null;
  const candidate = /^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`;
  try {
    const url = new URL(candidate);
    return (url.protocol === 'https:' || url.protocol === 'http:') && url.hostname.includes('.') ? url.href : null;
  } catch {
    return null;
  }
}

/** The link as people read it: host and path, without the scheme or a trailing slash. */
export function notificationLinkLabel(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

const REASON_IN_MESSAGE = /[.\s]*(?:(?:rejection\s+|overriding\s+)?reason\s*:|سبب الرفض\s*:|سبب\s*:)\s*.+$/is;

/** The message text without its trailing "Reason: …", for cards that show the reason on its own. */
export function messageWithoutReason(message: string): string {
  const text = message || '';
  const stripped = text.replace(REASON_IN_MESSAGE, '').trimEnd();
  if (!stripped) return text;
  return /[.!?؟]$/.test(stripped) ? stripped : `${stripped}.`;
}
